/**
 * Life Blueprint V2.9.9 — Migration Batch 07 (Sheet27:
 *   B71 diagnostic + B82/B83/B84 hidden-stem lookups + B85 composite-key
 *   lookup + B86/B87/B88 chained two-stage lookups + B89 nayin lookup +
 *   B90 band-classified reference lookup).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE (audited by user, 2026-02, amended for B39/B48/B71):
 *   Sheet27!B39  Sheet27!B48  Sheet27!B71
 *   Sheet27!B82  Sheet27!B83  Sheet27!B84
 *   Sheet27!B85
 *   Sheet27!B86  Sheet27!B87  Sheet27!B88
 *   Sheet27!B89
 *   Sheet27!B90
 *
 *   Nothing else. Not wired into the main V2.9.9 pipeline (still throws
 *   NOT_IMPLEMENTED). Batch 01-06 implementation files remain unchanged.
 *   D85 is captured as a DISPLAY_STATIC_CONSTANT only — NOT a
 *   CoreRuleEngine calculation node.
 *
 * SOURCE-OF-TRUTH POLICY (MIGRATION_RULES.md → Encoding Warning)
 *   Every Chinese literal in this file is constructed from its audited
 *   Unicode code points via `String.fromCodePoint(...)`. This is
 *   intentional and mandatory:
 *
 *     The Batch 07 audit fixture contained FIVE text-vs-Unicode
 *     mismatches (the plain-text glyphs and the AscW code points did
 *     NOT agree). Per MIGRATION_RULES.md, Unicode code points are the
 *     Golden Master. This module therefore encodes ONLY the code-point
 *     evidence — the plain-text glyphs from the fixture are documented
 *     below for traceability but MUST NOT drive behaviour.
 *
 *   Recorded discrepancies (fixture text → audited Unicode):
 *     B82   丙/戊/庚 → 丙/庚/戊  (positions 3 and 5 swapped)
 *     B83   火/土/金 → 火/金/土  (positions 3 and 5 swapped)
 *     B85   乙木     → 七杀      (entirely different 十神 term)
 *     B89   水中土   → 沙中土    (position 1: 水 U+6C34 vs 沙 U+6C99)
 *     B39   复核     → 核对      (positions swapped)
 *     B48   复核     → 核对      (positions swapped)
 *
 *   B36, B37, B38, B52, B84, B86, B87, B88, B71 all matched exactly.
 *
 * REFERENCE-DATA POLICY
 *   All reference rows are frozen as PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE.
 *   They are NOT written into `src/engine/referenceData.js`. Missing keys
 *   fail per the audited Excel contract:
 *     • B85, B89                 → IFERROR(..., "")   ⇒ empty-string on miss
 *     • B82/B83/B84 hidden stem  → bare VLOOKUP        ⇒ throw
 *                                   MISSING_REFERENCE_EVIDENCE (Batch 03/06 contract)
 *     • B86/B87/B88 chained      → inner throws on miss, outer IFERROR("")
 *     • B90                      → audited output row required; throws
 *                                   MISSING_REFERENCE_EVIDENCE for
 *                                   (band, B37) combinations not audited
 *   B90 empty-string is a VALID business result and MUST NOT be normalised
 *   to null / undefined / placeholder text.
 *
 * DETERMINISM
 *   No randomness, no wall clock, no LLM. Same input → byte-wise identical
 *   output.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { MANUAL_MODE_TOKEN } from "@/engine/v299/batch03_sheet27";

// ─── Audited string constants built ONLY from Unicode code points ────────────

/** ASCII underscore U+005F — composite-key separator (B85, B86/87/88 outer). */
const KEY_SEPARATOR = "_";

/** ASCII slash U+002F — aggregation separator (B82/B83/B84). */
const AGG_SEPARATOR = "/";

/** 藏干 (audited Unicode: 34255, 24178). */
const HIDDEN_STEM_SUFFIX = String.fromCodePoint(34255, 24178);

/**
 * B71 audited Unicode (BRANCH B — boundary-warning summary): 
 * "⚠️ 存在边界日警告，请查看上方年柱/月柱备注" (matches fixture text — no discrepancy).
 * Selected when C32 !== "手动覆盖" AND (B39 !== "" OR B48 !== "").
 */
const B71_WARNING_TEXT = String.fromCodePoint(
  9888, 65039, 32, 23384, 22312, 36793, 30028, 26085, 35686, 21578, 65292,
  35831, 26597, 30475, 19978, 26041, 24180, 26609, 47, 26376, 26609,
  22791, 27880
);

/**
 * B71 audited Unicode (BRANCH A — manual-override mode):
 * "ℹ️ 手动覆盖模式：节气边界警告不适用".
 * Selected when '01_个人资料'!$C$32 === MANUAL_MODE_TOKEN ("手动覆盖").
 * Encoded from AscW code points per MIGRATION_RULES.md → Encoding Warning.
 */
const B71_MANUAL_TEXT = String.fromCodePoint(
  8505, 65039, 32, 25163, 21160, 35206, 30422, 27169, 24335, 65306, 33410,
  27668, 36793, 30028, 35686, 21578, 19981, 36866, 29992
);

/**
 * B71 audited Unicode (BRANCH C — no boundary risk):
 * "✅ 未检测到边界日风险".
 * Selected when C32 !== "手动覆盖" AND B39 === "" AND B48 === "".
 */
const B71_NONE_TEXT = String.fromCodePoint(
  9989, 32, 26410, 26816, 27979, 21040, 36793, 30028, 26085, 39118, 38505
);

/**
 * B39 audited Unicode (TRUE branch of `=IF(F20="是", <warning>, "")`):
 * "⚠️ 出生日为立春当日，DB暂无精确时间，建议人工核对年柱".
 */
const B39_WARNING_TEXT = String.fromCodePoint(
  9888, 65039, 32, 20986, 29983, 26085, 20026, 31435, 26149, 24403, 26085,
  65292, 68, 66, 26242, 26080, 31934, 30830, 26102, 38388, 65292, 24314,
  35758, 20154, 24037, 26680, 23545, 24180, 26609
);

/**
 * B48 audited Unicode (TRUE branch of
 *   `=IF(COUNTIF(F19:F30,"是")>0, <warning>, "")`):
 * "⚠️ 出生日为节气交界当日，DB暂无精确时间，建议人工核对月柱".
 */
const B48_WARNING_TEXT = String.fromCodePoint(
  9888, 65039, 32, 20986, 29983, 26085, 20026, 33410, 27668, 20132, 30028,
  24403, 26085, 65292, 68, 66, 26242, 26080, 31934, 30830, 26102, 38388,
  65292, 24314, 35758, 20154, 24037, 26680, 23545, 26376, 26609
);

/**
 * '是' — audited Chinese "yes" token used inside F20 and F19:F30 predicates
 * as literal string comparison target. Constructed from Unicode.
 */
const YES_TOKEN = String.fromCodePoint(26159); // 是

/**
 * Sheet27!D85 canonical value (DISPLAY_STATIC_CONSTANT).
 *
 *   HasFormula = False
 *   Value length = 4
 *   Unicode: U+0022, U+65E5, U+4E3B, U+0022
 *   Canonical value: `"日主"`  (the ASCII double-quotes are PART of the
 *   actual Excel cell value — this is NOT a Python/JSON escape.)
 *
 * D85 is a static display label. It is NOT a CoreRuleEngine calculation
 * node. No formula, no VLOOKUP, no derivation. Recorded here so the
 * evidence is stored inside the Batch 07 slice for traceability without
 * introducing any computation.
 */
export const D85_DISPLAY_STATIC_CONSTANT = Object.freeze({
  cell: "Sheet27!D85",
  hasFormula: false,
  length: 4,
  unicode: [0x22, 0x65E5, 0x4E3B, 0x22],
  value: String.fromCodePoint(0x22, 0x65E5, 0x4E3B, 0x22),
  classification: "DISPLAY_STATIC_CONSTANT",
  note:
    "The literal ASCII double-quote characters are PART of the audited " +
    "Excel cell value. D85 is a static label, not a calculation node — " +
    "no rule migration required.",
});

/**
 * B82 audited Unicode: "丙/庚/戊" (fixture text "丙/戊/庚" ≠ Unicode — Unicode wins).
 * Composed slot-by-slot from the audited hidden-stem row so the
 * ordering is provable from the same evidence as B86/B87/B88.
 */
const B82_STEMS = String.fromCodePoint(19993, 47, 24218, 47, 25098);

/** B83 audited Unicode: "火/金/土" (fixture text "火/土/金" ≠ Unicode — Unicode wins). */
const B83_ELEMENTS = String.fromCodePoint(28779, 47, 37329, 47, 22303);

/** B84 audited Unicode: "6/3/1" (matches fixture text). */
const B84_NUMBERS = String.fromCodePoint(54, 47, 51, 47, 49);

/** B85 audited Unicode: "七杀" (fixture text "乙木" ≠ Unicode — Unicode wins). */
const B85_TEN_GOD = String.fromCodePoint(19971, 26432);

/** B86 audited Unicode: "正官" (matches fixture text). */
const B86_TEN_GOD = String.fromCodePoint(27491, 23448);

/** B87 audited Unicode: "劫财" (matches fixture text). */
const B87_TEN_GOD = String.fromCodePoint(21163, 36130);

/** B88 audited Unicode: "正印" (matches fixture text). */
const B88_TEN_GOD = String.fromCodePoint(27491, 21360);

/** B89 audited Unicode: "沙中土" (fixture text "水中土" ≠ Unicode — Unicode wins). */
const B89_NAYIN = String.fromCodePoint(27801, 20013, 22303);

// Audited individual glyphs used inside the partial reference tables.
const GLYPH_XIN  = String.fromCodePoint(36763); // 辛  (B52 current sample)
const GLYPH_DING = String.fromCodePoint(19969); // 丁  (B36 current sample)
const GLYPH_BING = String.fromCodePoint(19993); // 丙  (hidden stem)
const GLYPH_GENG = String.fromCodePoint(24218); // 庚  (hidden stem)
const GLYPH_WU   = String.fromCodePoint(25098); // 戊  (hidden stem)
const GLYPH_SI   = String.fromCodePoint(24051); // 巳  (B37 current sample)
const GLYPH_DINGSI = String.fromCodePoint(19969, 24051); // 丁巳 (B38 current sample)

// ─── PARTIAL Golden Master reference tables (Batch 07 only) ──────────────────

/**
 * Hidden-stem aggregation reference — required by B82/B83/B84 and by the
 * inner stage of B86/B87/B88.
 *
 * Provenance summary supplied by the user audit:
 *   • Audited formula key: <地支> & "_" & "藏干"
 *   • Reference worksheets: Sheet42 and Sheet43 (share the same key rows)
 *   • Aggregated columns produce B82 (天干), B83 (五行), B84 (数字)
 *   • Individual hidden-stem columns feed B86, B87, B88 respectively.
 *
 * Only the "巳_藏干" row required by the current Golden Master fixture is
 * recorded. Missing keys throw MISSING_REFERENCE_EVIDENCE.
 */
export const PARTIAL_HIDDEN_STEM_REFERENCE = Object.freeze({
  codeName: "Sheet42/Sheet43 (hidden-stem aggregate, audited rows only)",
  worksheetIndices: [42, 43],
  status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
  keySuffix: HIDDEN_STEM_SUFFIX,
  keySeparator: KEY_SEPARATOR,
  aggregationSeparator: AGG_SEPARATOR,
  note:
    "Composite key = <地支> & '_' & '藏干'. Aggregated columns B82/B83/B84 " +
    "concatenate the three hidden-stem slots (position 1 / position 2 / " +
    "position 3) with ASCII '/'. The individual innerStemForB86/B87/B88 " +
    "slots feed the outer Sheet45 chained lookup for B86/B87/B88. " +
    "Ordering is preserved exactly per audited Unicode (position 1=丙, " +
    "position 2=庚, position 3=戊 for the 巳 sample).",
  rows: Object.freeze([
    Object.freeze({
      key: GLYPH_SI + KEY_SEPARATOR + HIDDEN_STEM_SUFFIX, // 巳_藏干
      stems: B82_STEMS,       // 丙/庚/戊 — B82 aggregated
      elements: B83_ELEMENTS, // 火/金/土 — B83 aggregated
      numbers: B84_NUMBERS,   // 6/3/1  — B84 aggregated
      innerStemForB86: GLYPH_BING, // 丙 → outer 辛_丙 → 正官
      innerStemForB87: GLYPH_GENG, // 庚 → outer 辛_庚 → 劫财
      innerStemForB88: GLYPH_WU,   // 戊 → outer 辛_戊 → 正印
      sourceCells: {
        stems: "Sheet42/Sheet43 aggregated audited row",
        innerStemForB86: "Sheet42 (or Sheet43) hidden-stem primary column",
        innerStemForB87: "Sheet42 (or Sheet43) hidden-stem secondary column",
        innerStemForB88: "Sheet42 (or Sheet43) hidden-stem tertiary column",
      },
      auditedInBatch: "Batch07",
    }),
  ]),
});

/**
 * Sheet45 composite-key outer lookup — required by B85 and by the outer
 * stage of B86/B87/B88.
 *
 * Provenance:
 *   • Confirmed Excel formula for B85:
 *       IFERROR(VLOOKUP(B52 & "_" & B36, Sheet45!F:I, 4, FALSE), "")
 *   • Confirmed anchor for B85: Sheet45!F75 (matched), Sheet45!I75 (returned)
 *   • Semantic family: 十神 (U+5341 U+795E)
 *   • Row content for the audited fixture:
 *       辛_丁 → 七杀   (Unicode-authoritative; fixture text "乙木" was
 *                     inconsistent — Unicode wins)
 *       辛_丙 → 正官
 *       辛_庚 → 劫财
 *       辛_戊 → 正印
 *
 * Only the four keys required by the audited Golden Master fixture are
 * recorded. Missing keys return "" per IFERROR.
 */
export const PARTIAL_SHEET45_REFERENCE = Object.freeze({
  codeName: "Sheet45",
  worksheetIndex: 45,
  columnRange: "F:I",
  returnColumnIndex: 4, // I column
  status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
  keySeparator: KEY_SEPARATOR,
  keyPattern: "<日主B52> & '_' & <B36 (B85) | 藏干single (B86/87/88)>",
  errorSemantics: "IFERROR_EMPTY",
  note:
    "Audited composite-key outer lookup (十神 family). Only the four keys " +
    "required by the current Golden Master fixture are recorded — this is " +
    "NOT a complete Sheet45 migration. Anchor for B85: F75 (matched), " +
    "I75 (returned). B86/B87/B88 outer rows are audited from the same " +
    "family but their exact row indices were not supplied.",
  rows: Object.freeze([
    Object.freeze({
      key: GLYPH_XIN + KEY_SEPARATOR + GLYPH_DING, // 辛_丁
      value: B85_TEN_GOD, // 七杀
      sourceCells: { key: "Sheet45!F75", value: "Sheet45!I75" },
      usedBy: "B85",
      auditedInBatch: "Batch07",
    }),
    Object.freeze({
      key: GLYPH_XIN + KEY_SEPARATOR + GLYPH_BING, // 辛_丙
      value: B86_TEN_GOD, // 正官
      sourceCells: { key: "Sheet45!F<audited>", value: "Sheet45!I<audited>" },
      usedBy: "B86",
      auditedInBatch: "Batch07",
    }),
    Object.freeze({
      key: GLYPH_XIN + KEY_SEPARATOR + GLYPH_GENG, // 辛_庚
      value: B87_TEN_GOD, // 劫财
      sourceCells: { key: "Sheet45!F<audited>", value: "Sheet45!I<audited>" },
      usedBy: "B87",
      auditedInBatch: "Batch07",
    }),
    Object.freeze({
      key: GLYPH_XIN + KEY_SEPARATOR + GLYPH_WU, // 辛_戊
      value: B88_TEN_GOD, // 正印
      sourceCells: { key: "Sheet45!F<audited>", value: "Sheet45!I<audited>" },
      usedBy: "B88",
      auditedInBatch: "Batch07",
    }),
  ]),
});

/**
 * Sheet39 columns B:G — required by B89.
 *
 * Provenance:
 *   • Confirmed Excel formula: IFERROR(VLOOKUP(B38, Sheet39!B:G, 6, FALSE), "")
 *   • Confirmed anchor: Sheet39!B55 (matched key), Sheet39!G55 (returned)
 *   • 60甲子 (U+7532 U+5B50) DB reference — 纳音 family.
 *   • Row content for the audited fixture:
 *       丁巳 → 沙中土  (Unicode-authoritative; fixture text "水中土" was
 *                     inconsistent — Unicode wins; correct 沙中土 也符合
 *                     传统 纳音 for 丁巳)
 *
 * This reference block is INDEPENDENT of PARTIAL_SHEET39_REFERENCE_EVIDENCE
 * (Batch 04) which used columns A:F row 29. Same worksheet, different
 * column origin, different row — must NOT be merged without direct
 * evidence.
 */
export const PARTIAL_SHEET39_B_G_REFERENCE = Object.freeze({
  codeName: "Sheet39",
  worksheetIndex: 39,
  columnRange: "B:G",
  returnColumnIndex: 6, // G column
  status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
  errorSemantics: "IFERROR_EMPTY",
  note:
    "60甲子 纳音 lookup. Distinct from PARTIAL_SHEET39_REFERENCE_EVIDENCE " +
    "(Batch 04, columns A:F row 29). Only the single audited row 55 is " +
    "recorded. Missing keys return '' per IFERROR.",
  rows: Object.freeze([
    Object.freeze({
      key: GLYPH_DINGSI, // 丁巳
      value: B89_NAYIN, // 沙中土
      sourceCells: { key: "Sheet39!B55", value: "Sheet39!G55" },
      usedBy: "B89",
      auditedInBatch: "Batch07",
    }),
  ]),
});

/**
 * DB_空亡规则 (Sheet47) — B90 band-classified reference.
 *
 * Provenance (partial):
 *   • Confirmed worksheetIndex = 47, CodeName = Sheet47
 *   • Confirmed 6-band classifier on B51:
 *       ≤10 → 1, ≤20 → 2, ≤30 → 3, ≤40 → 4, ≤50 → 5, otherwise → 6
 *   • Partial rows: A2:A7 = 1..6, A4=3, C4=21, D4=30
 *   • E4 and F4 contain additional rule values but their exact contents
 *     were NOT supplied — do NOT infer them.
 *   • Confirmed audited output for the current fixture:
 *       (band=3, B37=巳) → ""   (empty-string, VALID business result)
 *
 * Because E4/F4 semantics are unaudited, the implementation records only
 * (band, B37) → output audited data points. Any combination outside the
 * audited set fails closed with MISSING_REFERENCE_EVIDENCE. This is per
 * MIGRATION_RULES.md control #12 (STOP instead of guessing).
 */
export const PARTIAL_BAND_RULE_TABLE = Object.freeze({
  codeName: "DB_空亡规则 (Sheet47)",
  worksheetIndex: 47,
  status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
  classifierEvidence: Object.freeze({
    "B51<=10": 1,
    "B51<=20": 2,
    "B51<=30": 3,
    "B51<=40": 4,
    "B51<=50": 5,
    otherwise: 6,
  }),
  partialRowEvidence: Object.freeze({
    "A2:A7": [1, 2, 3, 4, 5, 6],
    "A4": 3,
    "C4": 21,
    "D4": 30,
    "E4": "UNAUDITED_E4",
    "F4": "UNAUDITED_F4",
  }),
  note:
    "Only the audited (band, B37) → output data point required by the " +
    "current Golden Master fixture is recorded. E4/F4 rule semantics " +
    "remain unaudited — no inference permitted.",
  auditedOutputs: Object.freeze([
    Object.freeze({
      band: 3,
      b37Key: GLYPH_SI, // 巳
      b90Value: "", // VALID Golden Master result — do NOT normalise
      sourceRow: "Sheet47!A4:F4",
      auditedInBatch: "Batch07",
    }),
  ]),
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function requireString(name, v) {
  if (typeof v !== "string") {
    throw new TypeError(`[Batch07] ${name} must be a string, got ${typeof v}`);
  }
  return v;
}
function requireInt(name, v) {
  if (!Number.isInteger(v)) {
    throw new TypeError(`[Batch07] ${name} must be an integer`);
  }
  return v;
}

/**
 * Bare VLOOKUP (no IFERROR wrap). Missing keys throw
 * MISSING_REFERENCE_EVIDENCE. Used by B82/B83/B84 and by the inner stage
 * of B86/B87/B88 (which is a bare VLOOKUP inside the outer IFERROR).
 */
function vlookupOrThrow(refTable, key) {
  const row = refTable.rows.find((r) => r.key === key);
  if (!row) {
    const err = new Error(
      `[Batch07·VLOOKUP] Missing ${refTable.codeName} key ${JSON.stringify(key)}. ` +
        "Only PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE is migrated for " +
        "this table; the required row has not yet been audited. No result guessed."
    );
    err.code = "MISSING_REFERENCE_EVIDENCE";
    err.refTable = refTable.codeName;
    err.key = key;
    throw err;
  }
  return row;
}

/**
 * IFERROR(VLOOKUP(...), "") — audited Excel semantics for B85, B89 and
 * the outer stage of B86/B87/B88. Missing keys return "".
 */
function vlookupIferrorEmpty(refTable, key) {
  const row = refTable.rows.find((r) => r.key === key);
  return row ? row.value : "";
}

// ─── Compute functions ───────────────────────────────────────────────────────

/**
 * Sheet27!B39 = IF(F20 = "是", <audited warning>, "")
 *
 * Deterministic single-cell predicate on F20. F20 arrives as an audited
 * upstream Golden Master value.
 *
 * @param {string} upstreamValue_F20 audited Sheet27!F20 (typically "是" or "否")
 * @returns {string}
 */
export function computeB39(upstreamValue_F20) {
  requireString("upstreamValue_F20 (Sheet27!F20)", upstreamValue_F20);
  return upstreamValue_F20 === YES_TOKEN ? B39_WARNING_TEXT : "";
}

/**
 * Sheet27!B48 = IF(COUNTIF(F19:F30, "是") > 0, <audited warning>, "")
 *
 * Deterministic COUNTIF port. The Excel formula only tests whether ANY
 * cell in F19:F30 equals "是" — the exact count does not matter beyond
 * `> 0`. Preserved as a strict-equality count so behaviour matches Excel
 * exactly (only cells whose string value equals `是` are counted;
 * numbers, blanks, and other strings are NOT counted).
 *
 * Accepts either an array of 12 strings (F19..F30 in order) or an
 * object keyed `F19..F30`. Extraneous keys / lengths raise a TypeError
 * so a caller cannot silently rely on an off-by-one array.
 *
 * @param {string[] | Object<string,string>} upstreamRange_F19_F30
 * @returns {string}
 */
export function computeB48(upstreamRange_F19_F30) {
  const values = normaliseF19F30(upstreamRange_F19_F30);
  let count = 0;
  for (const v of values) if (v === YES_TOKEN) count++;
  return count > 0 ? B48_WARNING_TEXT : "";
}

function normaliseF19F30(range) {
  if (Array.isArray(range)) {
    if (range.length !== 12) {
      throw new TypeError(
        `[Batch07·B48] upstreamRange_F19_F30 array must contain 12 entries ` +
          `(F19..F30), got ${range.length}`
      );
    }
    return range.map((v, i) => {
      if (typeof v !== "string") {
        throw new TypeError(
          `[Batch07·B48] upstreamRange_F19_F30[${i}] (Sheet27!F${19 + i}) ` +
            `must be a string, got ${typeof v}`
        );
      }
      return v;
    });
  }
  if (range === null || typeof range !== "object") {
    throw new TypeError(
      "[Batch07·B48] upstreamRange_F19_F30 must be an array of 12 or an " +
        "object keyed F19..F30"
    );
  }
  const out = [];
  for (let i = 19; i <= 30; i++) {
    const key = `F${i}`;
    const v = range[key];
    if (typeof v !== "string") {
      throw new TypeError(
        `[Batch07·B48] upstreamRange_F19_F30.${key} (Sheet27!${key}) ` +
          `must be a string, got ${typeof v}`
      );
    }
    out.push(v);
  }
  return out;
}

/**
 * Sheet27!B71 (AUDITED, three deterministic branches).
 *
 * Excel Golden Master formula (reconstructed from AscW / Unicode evidence):
 *   =IF('01_个人资料'!$C$32 = "手动覆盖",
 *       "ℹ️ 手动覆盖模式：节气边界警告不适用",
 *       IF(OR(B39<>"", B48<>""),
 *          "⚠️ 存在边界日警告，请查看上方年柱/月柱备注",
 *          "✅ 未检测到边界日风险"))
 *
 * The previous fail-closed `UNAUDITED_B71_BRANCH` contract is SUPERSEDED
 * by this direct Excel formula evidence. All three branches are now
 * deterministic; `MANUAL_MODE_TOKEN` (audited "手动覆盖") is reused from
 * Batch 03.
 *
 * @param {string} upstreamC32          audited '01_个人资料'!$C$32
 * @param {string} upstreamValue_B39
 * @param {string} upstreamValue_B48
 * @returns {string}
 */
export function computeB71(upstreamC32, upstreamValue_B39, upstreamValue_B48) {
  requireString("upstreamC32 ('01_个人资料'!$C$32)", upstreamC32);
  requireString("upstreamValue_B39 (Sheet27!B39)", upstreamValue_B39);
  requireString("upstreamValue_B48 (Sheet27!B48)", upstreamValue_B48);
  if (upstreamC32 === MANUAL_MODE_TOKEN) {
    return B71_MANUAL_TEXT; // BRANCH A
  }
  if (upstreamValue_B39 !== "" || upstreamValue_B48 !== "") {
    return B71_WARNING_TEXT; // BRANCH B (Excel OR semantics)
  }
  return B71_NONE_TEXT; // BRANCH C
}

function lookupHiddenStemRow(upstreamValue_B37) {
  requireString("upstreamValue_B37 (Sheet27!B37)", upstreamValue_B37);
  const key = upstreamValue_B37 + KEY_SEPARATOR + HIDDEN_STEM_SUFFIX;
  return vlookupOrThrow(PARTIAL_HIDDEN_STEM_REFERENCE, key);
}

/** Sheet27!B82 — aggregated hidden-stem 天干 series ("<a>/<b>/<c>"). */
export function computeB82(upstreamValue_B37) {
  return lookupHiddenStemRow(upstreamValue_B37).stems;
}

/** Sheet27!B83 — aggregated hidden-stem 五行 series. */
export function computeB83(upstreamValue_B37) {
  return lookupHiddenStemRow(upstreamValue_B37).elements;
}

/** Sheet27!B84 — aggregated hidden-stem 数字 series (ASCII digits + '/'). */
export function computeB84(upstreamValue_B37) {
  return lookupHiddenStemRow(upstreamValue_B37).numbers;
}

/**
 * Sheet27!B85 = IFERROR(VLOOKUP(B52 & "_" & B36, Sheet45!F:I, 4, FALSE), "")
 */
export function computeB85(upstreamValue_B52, upstreamValue_B36) {
  requireString("upstreamValue_B52 (Sheet27!B52)", upstreamValue_B52);
  requireString("upstreamValue_B36 (Sheet27!B36)", upstreamValue_B36);
  const key = upstreamValue_B52 + KEY_SEPARATOR + upstreamValue_B36;
  return vlookupIferrorEmpty(PARTIAL_SHEET45_REFERENCE, key);
}

/**
 * Shared chained two-stage lookup driving B86/B87/B88.
 *
 * Inner: bare VLOOKUP of `B37 & "_藏干"` in the hidden-stem reference
 *        → single-character stem for the specified slot.
 * Outer: IFERROR(VLOOKUP(B52 & "_" & innerStem, Sheet45!F:I, 4, FALSE), "").
 */
function computeChainedTenGod(upstreamValue_B52, upstreamValue_B37, slotKey) {
  requireString("upstreamValue_B52 (Sheet27!B52)", upstreamValue_B52);
  const hiddenStemRow = lookupHiddenStemRow(upstreamValue_B37);
  const innerStem = hiddenStemRow[slotKey];
  if (typeof innerStem !== "string" || innerStem.length === 0) {
    // Defensive — audited reference row must supply the slot.
    const err = new Error(
      `[Batch07·chained] Audited hidden-stem row is missing slot '${slotKey}' ` +
        "for the required Golden Master fixture."
    );
    err.code = "MISSING_REFERENCE_EVIDENCE";
    err.slot = slotKey;
    throw err;
  }
  const outerKey = upstreamValue_B52 + KEY_SEPARATOR + innerStem;
  return vlookupIferrorEmpty(PARTIAL_SHEET45_REFERENCE, outerKey);
}

/** Sheet27!B86 — chained: inner=hidden-stem slot 1, outer=Sheet45. */
export function computeB86(upstreamValue_B52, upstreamValue_B37) {
  return computeChainedTenGod(upstreamValue_B52, upstreamValue_B37, "innerStemForB86");
}
/** Sheet27!B87 — chained: inner=hidden-stem slot 2, outer=Sheet45. */
export function computeB87(upstreamValue_B52, upstreamValue_B37) {
  return computeChainedTenGod(upstreamValue_B52, upstreamValue_B37, "innerStemForB87");
}
/** Sheet27!B88 — chained: inner=hidden-stem slot 3, outer=Sheet45. */
export function computeB88(upstreamValue_B52, upstreamValue_B37) {
  return computeChainedTenGod(upstreamValue_B52, upstreamValue_B37, "innerStemForB88");
}

/**
 * Sheet27!B89 = IFERROR(VLOOKUP(B38, Sheet39!B:G, 6, FALSE), "")
 */
export function computeB89(upstreamValue_B38) {
  requireString("upstreamValue_B38 (Sheet27!B38)", upstreamValue_B38);
  return vlookupIferrorEmpty(PARTIAL_SHEET39_B_G_REFERENCE, upstreamValue_B38);
}

/**
 * Six-band classifier of B51. Preserves the audited `<=` boundaries.
 * @param {number} B51 integer
 * @returns {1|2|3|4|5|6}
 */
export function classifyB51Band(upstreamValue_B51) {
  requireInt("upstreamValue_B51 (Sheet27!B51)", upstreamValue_B51);
  if (upstreamValue_B51 <= 10) return 1;
  if (upstreamValue_B51 <= 20) return 2;
  if (upstreamValue_B51 <= 30) return 3;
  if (upstreamValue_B51 <= 40) return 4;
  if (upstreamValue_B51 <= 50) return 5;
  return 6;
}

/**
 * Sheet27!B90 — DB_空亡规则 (Sheet47) band-classified reference lookup.
 *
 * Only the audited (band=3, B37=巳) → "" data point is available. For any
 * other combination, throws MISSING_REFERENCE_EVIDENCE. The empty-string
 * return for the audited combination is a VALID Golden Master result and
 * MUST NOT be normalised.
 *
 * @param {string} upstreamValue_B37
 * @param {number} upstreamValue_B51
 * @returns {string}
 */
export function computeB90(upstreamValue_B37, upstreamValue_B51) {
  requireString("upstreamValue_B37 (Sheet27!B37)", upstreamValue_B37);
  const band = classifyB51Band(upstreamValue_B51);
  const row = PARTIAL_BAND_RULE_TABLE.auditedOutputs.find(
    (r) => r.band === band && r.b37Key === upstreamValue_B37
  );
  if (!row) {
    const err = new Error(
      `[Batch07·B90] MISSING_REFERENCE_EVIDENCE for (band=${band}, B37=` +
        `${JSON.stringify(upstreamValue_B37)}). Only the audited fixture ` +
        "point (band=3, B37='巳') is available. No result guessed."
    );
    err.code = "MISSING_REFERENCE_EVIDENCE";
    err.band = band;
    err.B37 = upstreamValue_B37;
    throw err;
  }
  return row.b90Value; // may be "" — VALID
}

// ─── Aggregate ───────────────────────────────────────────────────────────────

/**
 * Aggregate Batch 07 entry point.
 *
 * B39 and B48 are now computed inside Batch 07 from their audited
 * upstream F-column inputs (F20 and F19:F30). B71 then consumes those
 * computed B39/B48 values together with the audited manual-mode token
 * (`upstreamC32`). For backwards compatibility with older test fixtures
 * that supplied B39/B48 directly, `upstreamValue_B39` / `upstreamValue_B48`
 * are honoured when the corresponding F-column input is absent.
 *
 * @param {{
 *   upstreamC32: string,
 *   upstreamValue_F20?: string,
 *   upstreamRange_F19_F30?: string[] | Object<string,string>,
 *   upstreamValue_B39?: string,
 *   upstreamValue_B48?: string,
 *   upstreamValue_B36: string,
 *   upstreamValue_B37: string,
 *   upstreamValue_B38: string,
 *   upstreamValue_B51: number,
 *   upstreamValue_B52: string
 * }} slice
 */
export function computeBatch07Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch07] slice must be an object");
  }
  const B39 =
    slice.upstreamValue_F20 !== undefined
      ? computeB39(slice.upstreamValue_F20)
      : (requireString("upstreamValue_B39 (fallback)", slice.upstreamValue_B39), slice.upstreamValue_B39);
  const B48 =
    slice.upstreamRange_F19_F30 !== undefined
      ? computeB48(slice.upstreamRange_F19_F30)
      : (requireString("upstreamValue_B48 (fallback)", slice.upstreamValue_B48), slice.upstreamValue_B48);
  const B71 = computeB71(slice.upstreamC32, B39, B48);
  const B82 = computeB82(slice.upstreamValue_B37);
  const B83 = computeB83(slice.upstreamValue_B37);
  const B84 = computeB84(slice.upstreamValue_B37);
  const B85 = computeB85(slice.upstreamValue_B52, slice.upstreamValue_B36);
  const B86 = computeB86(slice.upstreamValue_B52, slice.upstreamValue_B37);
  const B87 = computeB87(slice.upstreamValue_B52, slice.upstreamValue_B37);
  const B88 = computeB88(slice.upstreamValue_B52, slice.upstreamValue_B37);
  const B89 = computeB89(slice.upstreamValue_B38);
  const B90 = computeB90(slice.upstreamValue_B37, slice.upstreamValue_B51);
  return {
    B39,
    B48,
    B71,
    B82,
    B83,
    B84,
    B85,
    B86,
    B87,
    B88,
    B89,
    B90,
    _provenance: {
      excelSources: [
        "Sheet27!B39",
        "Sheet27!B48",
        "Sheet27!B71",
        "Sheet27!B82",
        "Sheet27!B83",
        "Sheet27!B84",
        "Sheet27!B85",
        "Sheet27!B86",
        "Sheet27!B87",
        "Sheet27!B88",
        "Sheet27!B89",
        "Sheet27!B90",
      ],
      formulas: {
        "Sheet27!B39":
          "=IF(F20=\"是\",\"⚠️ 出生日为立春当日，DB暂无精确时间，建议人工核对年柱\",\"\")",
        "Sheet27!B48":
          "=IF(COUNTIF(F19:F30,\"是\")>0,\"⚠️ 出生日为节气交界当日，DB暂无精确时间，建议人工核对月柱\",\"\")",
        "Sheet27!B71":
          "=IF('01_个人资料'!$C$32=\"手动覆盖\",\"ℹ️ 手动覆盖模式：节气边界警告不适用\"," +
          "IF(OR(B39<>\"\",B48<>\"\"),\"⚠️ 存在边界日警告，请查看上方年柱/月柱备注\",\"✅ 未检测到边界日风险\"))",
        "Sheet27!B82":
          "=VLOOKUP(B37 & '_' & '藏干', <Sheet42/Sheet43 hidden-stem table>, <aggregated 天干 col>, FALSE)",
        "Sheet27!B83":
          "=VLOOKUP(B37 & '_' & '藏干', <Sheet42/Sheet43 hidden-stem table>, <aggregated 五行 col>, FALSE)",
        "Sheet27!B84":
          "=VLOOKUP(B37 & '_' & '藏干', <Sheet42/Sheet43 hidden-stem table>, <aggregated 数字 col>, FALSE)",
        "Sheet27!B85": "=IFERROR(VLOOKUP(B52 & '_' & B36, Sheet45!F:I, 4, FALSE), '')",
        "Sheet27!B86":
          "=IFERROR(VLOOKUP(B52 & '_' & VLOOKUP(B37 & '_藏干', <Sheet42/Sheet43>, <slot1 col>, FALSE), Sheet45!F:I, 4, FALSE), '')",
        "Sheet27!B87":
          "=IFERROR(VLOOKUP(B52 & '_' & VLOOKUP(B37 & '_藏干', <Sheet42/Sheet43>, <slot2 col>, FALSE), Sheet45!F:I, 4, FALSE), '')",
        "Sheet27!B88":
          "=IFERROR(VLOOKUP(B52 & '_' & VLOOKUP(B37 & '_藏干', <Sheet42/Sheet43>, <slot3 col>, FALSE), Sheet45!F:I, 4, FALSE), '')",
        "Sheet27!B89": "=IFERROR(VLOOKUP(B38, Sheet39!B:G, 6, FALSE), '')",
        "Sheet27!B90":
          "=<band-classified reference lookup into DB_空亡规则 (Sheet47) driven by B37 and classifyB51Band(B51); exact E4/F4 rule semantics unaudited — audited fixture point only>",
      },
      keySeparator: KEY_SEPARATOR, // "_"
      aggregationSeparator: AGG_SEPARATOR, // "/"
      hiddenStemSuffix: HIDDEN_STEM_SUFFIX, // 藏干
      partialReferences: {
        HIDDEN_STEM: PARTIAL_HIDDEN_STEM_REFERENCE.status,
        SHEET45: PARTIAL_SHEET45_REFERENCE.status,
        SHEET39_B_G: PARTIAL_SHEET39_B_G_REFERENCE.status,
        BAND_RULE_TABLE: PARTIAL_BAND_RULE_TABLE.status,
      },
      staticDisplayConstants: {
        "Sheet27!D85": D85_DISPLAY_STATIC_CONSTANT.classification,
      },
      auditedB71Contract:
        "SUPERSEDES the previous UNAUDITED_B71_BRANCH fail-closed. Three " +
        "deterministic branches: (A) C32==='手动覆盖' → manual-mode " +
        "notice; (B) C32!==manual AND (B39!=='' OR B48!=='') → boundary " +
        "warning (Excel OR semantics); (C) C32!==manual AND B39==='' AND " +
        "B48==='' → no-risk notice.",
      encodingWarning:
        "All Chinese literals are produced from audited AscW code points via " +
        "String.fromCodePoint. Five text-vs-Unicode discrepancies in the " +
        "Batch 07 fixture (B82, B83, B85, B89, and 复核/核对 inside B39/B48) " +
        "were resolved in favour of the Unicode evidence per MIGRATION_RULES.md.",
    },
  };
}
